import { useEffect, useMemo, useState, type FC } from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import {
  BulkCreateEmployeesLocationWorkAreasDocument,
  GetWorkAreasForLocationDocument,
  ProfileEmployeeLocationEmployeesLocationFragment,
  ProfilePageDocument,
  SetAsDefaultEmployeesLocationDocument,
} from 'generated/resources/graphql'
import { useLazyQuery } from '@apollo/client'

import Modal from 'modules/core/components/Modal'
import Selector from 'modules/core/components/Selector'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import { useMutation as useProfileMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface SetDefaultLocationsModalProps {
  employeeLocations: ProfileEmployeeLocationEmployeesLocationFragment[]
  employeesLocation: ProfileEmployeeLocationEmployeesLocationFragment
  employeeId: string
  onClose: () => void
  onSuccess: (locationId: string) => void
}

const SetDefaultLocationsModal: FC<SetDefaultLocationsModalProps> = ({
  employeeLocations,
  employeesLocation,
  employeeId: _employeeId,
  onClose,
  onSuccess,
}) => {
  const initialLocationId = employeesLocation.location?.id ?? ''
  const initialDefaultWorkAreaId =
    employeesLocation.employeesLocationWorkAreas?.find((wa) => wa.isDefault)?.workArea?.id ?? null

  const [selectedLocationId, setSelectedLocationId] = useState(initialLocationId)
  const [selectedWorkAreaId, setSelectedWorkAreaId] = useState<string | null>(
    initialDefaultWorkAreaId
  )

  const employeesLocationId = employeesLocation.id
  const employeesLocationLocationId = employeesLocation.location?.id ?? ''
  const employeesLocationDefaultWorkAreaId =
    employeesLocation.employeesLocationWorkAreas?.find((wa) => wa.isDefault)?.workArea?.id ?? null

  useEffect(() => {
    setSelectedLocationId(employeesLocationLocationId)
    setSelectedWorkAreaId(employeesLocationDefaultWorkAreaId)
  }, [employeesLocationId, employeesLocationLocationId, employeesLocationDefaultWorkAreaId])

  const [fetchWorkAreas, { data: workAreasData }] = useLazyQuery(GetWorkAreasForLocationDocument, {
    fetchPolicy: 'network-only',
  })

  useEffect(() => {
    if (selectedLocationId) {
      fetchWorkAreas({
        variables: {
          locationIds: [selectedLocationId],
          onlyNonArchived: true,
        },
        fetchPolicy: 'network-only',
      })
      if (selectedLocationId !== initialLocationId) {
        setSelectedWorkAreaId(null)
      }
    }
  }, [selectedLocationId, initialLocationId, fetchWorkAreas])

  const workAreaOptions = useMemo(() => {
    return (workAreasData?.locations?.workAreas ?? []).map((wa) => ({
      value: wa.id,
      label: wa.name,
    }))
  }, [workAreasData?.locations?.workAreas])

  const [bulkCreateWorkAreas] = useProfileMutation(BulkCreateEmployeesLocationWorkAreasDocument, {
    extractorFn: (data) => data.locationsMutations.bulkCreateLocationsEmployeesLocationWorkArea,
  })

  const [setAsDefault] = useProfileMutation(SetAsDefaultEmployeesLocationDocument, {
    extractorFn: (data) => data.locationsMutations.setAsDefaultLocationsEmployeesLocation,
    refetchQueries: [ProfilePageDocument],
  })

  const resolveEmployeeLocationWorkAreaId = async (
    junction: ProfileEmployeeLocationEmployeesLocationFragment,
    workAreaId: string | null
  ): Promise<string | undefined> => {
    if (workAreaId == null) return undefined
    const junctionId = junction.id
    if (junctionId == null) return undefined
    const bulkResult = await bulkCreateWorkAreas({
      variables: {
        employeesLocationId: junctionId,
        workAreaIds: [workAreaId],
      },
    })
    const handled = handleResult(bulkResult, () => {})
    if (!handled.success) return undefined
    const created = handled.data?.employeesLocationWorkAreas?.[0]
    return created?.id
  }

  const onSubmit = async (closePortal: ClosePortalFn) => {
    const locationIdAtSubmit = selectedLocationId
    const workAreaIdAtSubmit = selectedWorkAreaId

    const resolvedJunction = employeeLocations.find((el) => el.location?.id === locationIdAtSubmit)
    if (!resolvedJunction?.id) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('profile.employee.workplaces.set_default_error'),
      })
      return
    }

    const employeeLocationWorkAreaId = await resolveEmployeeLocationWorkAreaId(
      resolvedJunction,
      workAreaIdAtSubmit
    )
    if (workAreaIdAtSubmit != null && employeeLocationWorkAreaId == null) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('profile.employee.workplaces.set_default_error'),
      })
      return
    }

    const result = await setAsDefault({
      variables: {
        employeeLocationId: resolvedJunction.id,
        employeeLocationWorkAreaId: employeeLocationWorkAreaId ?? undefined,
      },
    })

    const handled = handleResult(result, (errors) => {
      if (errors) {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('profile.employee.workplaces.set_default_error'),
        })
      }
    })

    if (handled.success && resolvedJunction.location?.id != null) {
      onSuccess(resolvedJunction.location.id)
      closePortal()
      app.setFlash({
        type: 'correct',
        message: i18n.t('employee_form.workplaces.set_default'),
      })
    }
  }

  const locationOptions = employeeLocations
    .map((el) => el.location)
    .filter((loc): loc is NonNullable<typeof loc> => loc != null)
    .map((loc) => ({
      value: loc.id,
      label: loc.name,
    }))

  return (
    <Modal
      illustration="document-edit"
      title={i18n.t('employee_form.workplaces.set_default_locations_title')}
      description={i18n.t('employee_form.workplaces.set_default_locations_description')}
      onSubmit={onSubmit}
      onClose={onClose}
      footer={
        <Box flexDirection="row" gap="s8" justifyContent="flexEnd">
          <Button type="secondary" onClick={onClose} data-testid="set-default-locations-cancel">
            {i18n.t('common.cancel')}
          </Button>
          <Button submit data-testid="set-default-locations-submit">
            {i18n.t('employee_form.workplaces.set_defaults_cta')}
          </Button>
        </Box>
      }
    >
      <Box flexDirection="column" gap="s16" width="full">
        <Box flexDirection="column" gap="s4" width="full">
          <Text.Normal weight="medium">
            {i18n.t('employee_form.workplaces.workplace_label')}
          </Text.Normal>
          <Selector.Popover
            data-testid="set-default-workplace-select"
            placeholder={i18n.t('employee_form.workplaces.select_default_workplace')}
            blankLabel={i18n.t('employee_form.workplaces.select_default_workplace')}
            mandatory
            value={selectedLocationId ? [selectedLocationId] : []}
            onChange={(newValue) => {
              const id = Array.isArray(newValue) ? newValue[0] : newValue
              setSelectedLocationId(id != null ? String(id) : '')
            }}
            isMultiSelect={false}
          >
            {locationOptions.map((opt) => (
              <Selector.Option
                key={opt.value}
                value={opt.value}
                label={opt.label}
                searchValue={opt.label}
              >
                {opt.label}
              </Selector.Option>
            ))}
          </Selector.Popover>
        </Box>
        <Box flexDirection="column" gap="s4" width="full">
          <Text.Normal weight="medium">
            {i18n.t('employee_form.workplaces.workarea_label')}
          </Text.Normal>
          <Selector.Popover
            key={`workarea-${selectedLocationId}`}
            data-testid="set-default-workarea-select"
            placeholder={i18n.t('employee_form.workplaces.select_default_workarea')}
            blankLabel={i18n.t('employee_form.workplaces.select_default_workarea')}
            value={selectedWorkAreaId != null ? [selectedWorkAreaId] : []}
            onChange={(newValue) => {
              const id = Array.isArray(newValue) ? newValue[0] : newValue
              setSelectedWorkAreaId(id != null ? String(id) : null)
            }}
            isMultiSelect={false}
            isDisabled={!selectedLocationId}
          >
            {workAreaOptions.map((opt) => (
              <Selector.Option
                key={opt.value}
                value={opt.value}
                label={opt.label}
                searchValue={opt.label}
              >
                {opt.label}
              </Selector.Option>
            ))}
          </Selector.Popover>
        </Box>
      </Box>
    </Modal>
  )
}

export default SetDefaultLocationsModal
